import { Component, ElementRef, EventEmitter, Input, Output, ViewChild, forwardRef, Optional, Self, TemplateRef, OnInit, OnDestroy } from '@angular/core';
import { ControlValueAccessor, FormControl, NgControl, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Observable, Subscription, forkJoin, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, startWith, switchMap, catchError, takeUntil } from 'rxjs/operators';
import { AppDataService, ProcedureCode } from 'src/app/core/services/app-data.service';
import { AssistantOption } from 'src/app/core/services/billing.service';
import { AssistantDetails, CodePromptingService, TimeDetails } from 'src/app/core/services/code-prompting.service';
import { DisciplineConfigService } from 'src/app/core/services/discipline-config.service';
import { MedicationService } from 'src/app/core/services/medication.service';
import { FeedbackService } from 'src/app/core/services/feedback.service';
import { UserService } from 'src/app/core/services/user.service';
import { ContactService } from 'src/app/core/services/contact.service';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { MedpraxService, Medicine, Material } from 'src/app/core/invoicing/medprax.invoicing.service';
import { TITLES } from 'src/app/core/data/app.data';
import { NgxMaterialTimepickerTheme } from 'ngx-material-timepicker';
import { ThemeService } from 'src/app/core/services/theme.service';
import { GroupService } from 'src/app/core/services/group.service';

@Component({
  selector: 'app-code-search-selector',
  templateUrl: './code-search-selector.component.html',
  styleUrls: ['./code-search-selector.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CodeSearchSelectorComponent),
      multi: true
    },
    {
      provide: MatFormFieldControl,
      useExisting: CodeSearchSelectorComponent
    }
  ]
})
export class CodeSearchSelectorComponent implements ControlValueAccessor, MatFormFieldControl<any>, OnInit, OnDestroy {
  @Input() patient: any = {};
  @Input() searchType: 'procedure' | 'medication' | 'material' | 'both' = 'both';
  @Input() modifiersOnly: boolean = false;
  @Input() hintText: string = '';
  @Output() codeSelected = new EventEmitter<any>();

  @ViewChild('inputElement') inputElement!: ElementRef<HTMLInputElement>;
  @ViewChild('codeBMIDialog') codeBMIDialog?: TemplateRef<any>;
  @ViewChild('codeTimeDialog') codeTimeDialog?: TemplateRef<any>;
  @ViewChild('codeAssistantDialog') codeAssistantDialog?: TemplateRef<any>;

  codeAdditionalInfo: any = {};
  assistantAdditionalInfo: AssistantDetails = { assistantName: '', assistantPracticeNumber: '' };
  previousAssistants: AssistantOption[] = [];
  filteredAssistants: AssistantOption[] = [];
  disciplineCode: string = 'medical';

  titles = TITLES;
  contactMessage: any = {};
  @ViewChild('contactDialog') contactDialog?: TemplateRef<any>;

  searchControl = new FormControl();
  filteredResults: Observable<any[]>;
  searchCompleted: boolean = false;

  // ControlValueAccessor properties
  value: any;
  disabled = false;
  onChange: any = () => { };
  onTouched: any = () => { };

  // MatFormFieldControl properties
  static nextId = 0;
  id = `code-search-selector-${CodeSearchSelectorComponent.nextId++}`;
  stateChanges = new Subject<void>();
  focused = false;
  errorState = false;
  controlType = 'code-search-selector';
  autofilled = false;
  userAriaDescribedBy = '';
  placeholder = '';
  required = false;
  group!: any;
  private themeSub?: Subscription;
  darkTheme!: NgxMaterialTimepickerTheme;

  private destroy$ = new Subject<void>();

  constructor(
    private appDataService: AppDataService,
    private medicationService: MedicationService,
    private dialog: MatDialog,
    private userService: UserService,
    private groupService: GroupService,
    private contactService: ContactService,
    private feedbackService: FeedbackService,
    @Optional() @Self() public ngControl: NgControl,
    private medpraxService: MedpraxService,
    private themeService: ThemeService,
    private codePromptingService: CodePromptingService,
    private disciplineConfigService: DisciplineConfigService
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.filteredResults = this.searchControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(value => this.filterResults(value))
    );
  }

  ngOnInit(): void {

    this.groupService.selectedGroup$
      .pipe(takeUntil(this.destroy$))
      .subscribe(group => {
        if (group) {
          this.group = group;
          this.disciplineCode = this.disciplineConfigService.getDisciplineCodeForSpeciality(group.groupSpeciality);
        }
      });

    this.themeSub = this.themeService.isDarkMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((isDarkMode) => {
        this.darkTheme = this.themeService.getTimepickerTheme(isDarkMode);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // MatFormFieldControl methods
  get empty(): boolean {
    return !this.searchControl.value;
  }

  get shouldLabelFloat(): boolean {
    return this.focused || !this.empty;
  }

  setDescribedByIds(ids: string[]): void {
    this.userAriaDescribedBy = ids.join(' ');
  }

  onContainerClick(event: MouseEvent): void {
    if ((event.target as Element).tagName.toLowerCase() !== 'input') {
      this.inputElement.nativeElement.focus();
    }
  }

  // ControlValueAccessor methods
  writeValue(value: any): void {
    this.value = value;
    this.searchControl.setValue(value);
    this.stateChanges.next();
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.searchControl.disable();
    } else {
      this.searchControl.enable();
    }
    this.stateChanges.next();
  }

  private filterResults(value: string): Observable<any[]> {
    this.searchCompleted = false;

    if (!value || value.length < 2) {
      return of([]);
    }

    if (this.searchType === 'procedure') {
      return this.appDataService.searchCodes(value, 'pc', this.modifiersOnly, undefined, this.group?.groupSpeciality)
        .pipe(
          catchError(error => {
            console.error('Error searching procedure codes:', error);
            this.searchCompleted = true;
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          }),
          map(codes => {
            this.searchCompleted = true;
            return codes.map(code => ({ ...code, type: 'procedure' }));
          })
        );
    } else if (this.searchType === 'medication') {
      return this.medpraxService.getProductPrice(value, 'medicine')
        .pipe(
          catchError(error => {
            console.error('Error searching medications:', error);
            this.searchCompleted = true;
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          }),
          map((medicines: Medicine[]) => {
            this.searchCompleted = true;
            return medicines.map(med => ({ ...med, type: 'medication' }));
          })
        );
    } else if (this.searchType === 'material') {
      return this.medpraxService.getProductPrice(value, 'material')
        .pipe(
          catchError(error => {
            console.error('Error searching materials:', error);
            this.searchCompleted = true;
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          }),
          map((materials: Material[]) => {
            this.searchCompleted = true;
            return materials.map(mat => ({ ...mat, type: 'material' }));
          })
        );
    } else {
      // Search everything in parallel
      return forkJoin({
        procedures: this.appDataService.searchCodes(value, 'pc', this.modifiersOnly, undefined, this.group?.groupSpeciality).pipe(
          catchError(error => {
            console.error('Error searching procedure codes:', error);
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          })
        ),
        medicines: this.medpraxService.getProductPrice(value, 'medicine').pipe(
          catchError(error => {
            console.error('Error searching medications:', error);
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          })
        ),
        materials: this.medpraxService.getProductPrice(value, 'material').pipe(
          catchError(error => {
            console.error('Error searching materials:', error);
            this.feedbackService.showToast('Error searching codes. Please try again.');
            return of([]);
          })
        )
      }).pipe(
        map(({ procedures, medicines, materials }) => {
          this.searchCompleted = true;
          return [
            ...procedures.map(code => ({ ...code, type: 'procedure' })),
            ...(medicines as Medicine[]).map(med => ({ ...med, type: 'medication' })),
            ...(materials as Material[]).map(mat => ({ ...mat, type: 'material' }))
          ];
        })
      );
    }
  }

  async onOptionSelected(item: any): Promise<void> {
    this.value = item;
    this.onChange(item);
    this.onTouched();
    if (this.codePromptingService.shouldShowTimeFields(item.code.toString())) {
      const startEndDetails = await this.openStartFinishDialog();
      if (!startEndDetails) {
        return;
      }
      item.startTime = startEndDetails.startTime;
      item.endTime = startEndDetails.endTime;
    }
    // Cancelling only skips the name — the code itself is still emitted.
    if (this.codePromptingService.shouldShowAssistantFields(item.code.toString(), this.disciplineCode)) {
      const assistantDetails = await this.openAssistantDialog();
      item.assistantName = assistantDetails?.assistantName || '';
      item.assistantPracticeNumber = assistantDetails?.assistantPracticeNumber || '';
    }
    if (item.code.toString() === '0018') {
      const bmiDetails = await this.openBMIDialog();
      item.weight = bmiDetails.weight;
      item.height = bmiDetails.height;
      item.BMI = bmiDetails.BMI;
    }
    this.codeSelected.emit(item);
    this.stateChanges.next();
    this.resetInput();
  }

  resetInput(): void {
    setTimeout(() => {
      this.inputElement.nativeElement.value = '';
      this.searchControl.reset();
      this.searchCompleted = false;
      this.stateChanges.next();
    });
  }


  getDisplayText(item: any): string {
    if (item.type === 'procedure') {
      return `${item.code} - ${item.description}`;
    } else if (item.type === 'medication') {
      const strength = item.strengthMetric1 ? `${item.strengthMetric1}${item.unitOfMeasure1}` : '';
      const volume = item.volumeMetric3 ? `${item.volumeMetric3}${item.unitOfMeasure3}` : '';
      const presentation = item.presentationCode || '';
      const packSize = item.standardPacksize ? `(Pack: ${item.standardPacksize})` : '';

      return `${item.name} ${strength} ${volume} ${presentation} ${packSize}`.trim();
    } else if (item.type === 'material') {
      const size = item.productSize ? `Size: ${item.productSize}` : '';
      const packSize = item.standardPacksize ? `(Pack: ${item.standardPacksize})` : '';
      const catalogue = item.catalogueNumber ? `Cat: ${item.catalogueNumber}` : '';

      return `${item.name} ${size} ${packSize} ${catalogue}`.trim();
    }
    return '';
  }

  getSubText(item: any): string {
    if (item.type === 'procedure') {
      return '';
    } else if (item.type === 'medication') {
      const parts = [];

      // Add NAPPI code
      if (item.nappiCode) {
        parts.push(`NAPPI: ${item.nappiCode}`);
      }

      // Add drug schedule
      if (item.drugSchedule) {
        parts.push(`Schedule ${item.drugSchedule}`);
      }

      // Add manufacturer
      if (item.manufacturerCode) {
        parts.push(`Mfr: ${item.manufacturerCode}`);
      }

      // Add generic indicator
      if (item.genericIndicatorCode) {
        parts.push(`Generic: ${item.genericIndicatorCode === 'G' ? 'Yes' : 'No'}`);
      }

      // Add scripting dosage if available
      if (item.scriptingDosage) {
        parts.push(`Dosage: ${item.scriptingDosage}`);
      }

      return parts.join(' | ');
    } else if (item.type === 'material') {
      const parts = [];

      // Add NAPPI code if available
      if (item.nappiCode) {
        parts.push(`NAPPI: ${item.nappiCode}`);
      }

      // Add barcode if available
      if (item.barcode) {
        parts.push(`Barcode: ${item.barcode}`);
      }

      // Add manufacturer
      if (item.manufacturerCode) {
        parts.push(`Mfr: ${item.manufacturerCode}`);
      }

      // Add full description if available
      if (item.fullDescription) {
        parts.push(`Desc: ${item.fullDescription}`);
      }

      return parts.join(' | ');
    }
    return '';
  }

  private async openStartFinishDialog(): Promise<TimeDetails | null> {
    this.codeAdditionalInfo = { startTime: '', endTime: '' };

    return this.codePromptingService.openStartFinishDialog(this.codeTimeDialog!);
  }

  private async openAssistantDialog(): Promise<AssistantDetails | null> {
    this.assistantAdditionalInfo = { assistantName: '', assistantPracticeNumber: '' };
    this.previousAssistants = await this.codePromptingService.getPreviousAssistants(this.group?.id);
    this.filterAssistants('');

    return this.codePromptingService.openAssistantDialog(this.codeAssistantDialog!);
  }

  filterAssistants(value: string): void {
    const filterValue = (value || '').toLowerCase();
    this.filteredAssistants = this.previousAssistants.filter(assistant => assistant.name.toLowerCase().includes(filterValue));
  }

  onAssistantSelected(name: string): void {
    const selected = this.previousAssistants.find(assistant => assistant.name === name);
    if (selected?.practiceNumber) {
      this.assistantAdditionalInfo.assistantPracticeNumber = selected.practiceNumber;
    }
  }

  private async openBMIDialog(): Promise<any> {
    this.codeAdditionalInfo = { weight: this.patient?.weight || '', height: this.patient?.height || '', BMI: '' };
    this.calculateBMI();
    const ref = this.dialog.open(this.codeBMIDialog!, { autoFocus: false, disableClose: true });
    const results = await ref.afterClosed().toPromise()
    if (results) {
      return this.codeAdditionalInfo;
    }
    else {
      return null;
    }
  }

  calculateBMI() {
    const weight = this.codeAdditionalInfo.weight;
    const height = this.codeAdditionalInfo.height / 100; // Convert height from cm to meters

    if (weight && height) {
      this.codeAdditionalInfo.BMI = (weight / (height * height)).toFixed(2).toString();
    } else {
      this.codeAdditionalInfo.BMI = null;
    }
  }


  openContactUsDialog(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();
    const user = this.userService.user;
    this.contactMessage = { title: user?.title || '', name: user?.name || '', surname: user?.surname || '', email: user?.email || '', phone: '', message: '' };
    this.dialog.open(this.contactDialog!, { width: '600px' });
  }

  sendContactMessage(): void {

    const sub = this.contactService.sendContactEmail(this.contactMessage)
      .subscribe(
        (response: any) => {
          this.feedbackService.showToast('Your contact request has been sent successfully.');
          sub.unsubscribe();
        },
        (error) => {
          this.feedbackService.showToast('There was an error submitting your contact request. Please try again.');
          sub.unsubscribe();
        }
      );
    this.dialog.closeAll(); // Close the edit colleague dialog
  }
} 